import { useCallback, useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
} from "antd";
import dayjs from "dayjs";
import { TailSpin } from "react-loader-spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faFloppyDisk,
  faPhone,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../contexts/auth";
import { useToasts } from "../../contexts/toasts";
import { Users } from "../../api/Users";
import { Sesso } from "../../enums/Sesso";
import { RoleLabels } from "../../enums/Roles";
import Avatar from "../../Components/Avatar";
import "./AreaPersonale.css";

export default function AreaPersonale() {
  const { loggedUser, setLoggedUser } = useAuth();
  const { showToast } = useToasts();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  // Prefill once the current user is available (fetched by AppRouter via
  // Users.getUserInfo). birthDate is normalised to a dayjs for the DatePicker.
  useEffect(() => {
    if (!loggedUser) return;
    form.setFieldsValue({
      nome: loggedUser.nome,
      cognome: loggedUser.cognome,
      birthDate: loggedUser.birthDate ? dayjs(loggedUser.birthDate) : null,
      sesso: loggedUser.sesso || undefined,
      peso: loggedUser.peso ?? undefined,
      altezza: loggedUser.altezza ?? undefined,
      email: loggedUser.email,
      phoneNumber: loggedUser.phoneNumber ?? "",
      username: loggedUser.username,
    });
  }, [loggedUser, form]);

  const handleSubmit = useCallback(
    (values: any) => {
      if (!loggedUser?.id) return;
      setSaving(true);
      const payload = {
        nome: values.nome,
        cognome: values.cognome,
        birthDate: values.birthDate
          ? values.birthDate.toISOString()
          : undefined,
        sesso: values.sesso,
        peso: values.peso ?? undefined,
        altezza: values.altezza ?? undefined,
        email: values.email,
        phoneNumber: values.phoneNumber || undefined,
        username: values.username,
      };
      Users.updateUser(loggedUser.id, payload).then(
        () => {
          // Re-fetch the canonical user so the sidebar/name and every consumer
          // of `loggedUser` reflect the change immediately.
          Users.getUserInfo()
            .then(({ data }) => setLoggedUser(data.user))
            .catch(() => {
              /* keep the optimistic local state on refetch failure */
              setLoggedUser((prev) => (prev ? { ...prev, ...payload } : prev));
            })
            .finally(() => {
              setSaving(false);
              showToast(
                "profile-success",
                "Dati aggiornati con successo",
                "",
                "success",
              );
            });
        },
        () => {
          setSaving(false);
          showToast(
            "profile-error",
            "Errore nell'aggiornamento dei dati",
            "",
            "danger",
          );
        },
      );
    },
    [loggedUser, setLoggedUser, showToast],
  );

  if (!loggedUser) {
    return (
      <Container fluid="md" className="mt-5 d-flex justify-content-center">
        <TailSpin height={64} width={64} color="#9c1419" radius="1" />
      </Container>
    );
  }

  return (
    <Container fluid="md" className="area-personale mt-4 mt-md-5">
      {/* Athletic profile header */}
      <header className="ap-header">
        <div className="ap-header-identity">
          <div className="ap-avatar">
            <Avatar user={loggedUser} />
          </div>
          <div className="ap-header-text">
            <span className="ap-header-role">{RoleLabels[loggedUser.role]}</span>
            <h1 className="ap-header-name">
              {loggedUser.nome} {loggedUser.cognome}
            </h1>
            <div className="ap-header-meta">
              <span className="ap-meta-chip">
                <FontAwesomeIcon icon={faEnvelope} />
                {loggedUser.email}
              </span>
              {loggedUser.phoneNumber && (
                <span className="ap-meta-chip">
                  <FontAwesomeIcon icon={faPhone} />
                  {loggedUser.phoneNumber}
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
        className="ap-form"
      >
        <div className="ap-grid">
          <section className="ap-card">
            <div className="ap-card-head">
              <span className="ap-card-tick" aria-hidden="true" />
              <h2 className="ap-card-title">Dati anagrafici</h2>
            </div>
            <div className="ap-card-body">
              <Form.Item
                label="Nome"
                name="nome"
                rules={[{ required: true, message: "Il nome è obbligatorio" }]}
              >
                <Input size="large" placeholder="Nome..." />
              </Form.Item>
              <Form.Item
                label="Cognome"
                name="cognome"
                rules={[{ required: true, message: "Il cognome è obbligatorio" }]}
              >
                <Input size="large" placeholder="Cognome..." />
              </Form.Item>
              <Form.Item label="Data di nascita" name="birthDate">
                <DatePicker
                  size="large"
                  className="w-100"
                  format="DD/MM/YYYY"
                  placeholder="Data di nascita..."
                />
              </Form.Item>
              <Form.Item label="Sesso" name="sesso">
                <Select
                  size="large"
                  placeholder="Sesso..."
                  allowClear
                  options={[
                    { label: "Uomo", value: Sesso.Male },
                    { label: "Donna", value: Sesso.Female },
                  ]}
                />
              </Form.Item>
              <div className="ap-row-2">
                <Form.Item label="Peso" name="peso">
                  <InputNumber
                    size="large"
                    className="w-100"
                    min={0}
                    step={0.5}
                    addonAfter="Kg"
                    placeholder="Peso..."
                  />
                </Form.Item>
                <Form.Item label="Altezza" name="altezza">
                  <InputNumber
                    size="large"
                    className="w-100"
                    min={0}
                    step={0.01}
                    addonAfter="m"
                    placeholder="Altezza..."
                  />
                </Form.Item>
              </div>
            </div>
          </section>

          <section className="ap-card">
            <div className="ap-card-head">
              <span className="ap-card-tick" aria-hidden="true" />
              <h2 className="ap-card-title">Dati di contatto</h2>
            </div>
            <div className="ap-card-body">
              <Form.Item
                label="E-mail"
                name="email"
                rules={[
                  { required: true, message: "L'indirizzo mail è obbligatorio" },
                  { type: "email", message: "Inserisci un indirizzo mail valido" },
                ]}
              >
                <Input size="large" type="email" placeholder="E-mail..." />
              </Form.Item>
              <Form.Item
                label="Numero di telefono"
                name="phoneNumber"
                rules={[
                  {
                    pattern: /^[0-9]{9,15}$/,
                    message: "Inserisci un numero di telefono valido",
                  },
                ]}
              >
                <Input size="large" placeholder="Numero di telefono..." />
              </Form.Item>
              <Form.Item
                label="Username"
                name="username"
                rules={[{ required: true, message: "L'username è obbligatorio" }]}
              >
                <Input
                  size="large"
                  prefix={<FontAwesomeIcon icon={faUser} className="ap-input-ic" />}
                  placeholder="Username..."
                />
              </Form.Item>
            </div>
          </section>
        </div>

        <div className="ap-actions">
          <Button
            type="primary"
            size="large"
            htmlType="submit"
            loading={saving}
            icon={<FontAwesomeIcon icon={faFloppyDisk} />}
          >
            Salva modifiche
          </Button>
        </div>
      </Form>
    </Container>
  );
}
